'use client';

import Image from 'next/image';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FC, useState, useMemo, useCallback } from 'react';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { FormLabel } from '@/components/common/FormLabel';
import { FormAutocomplete, FormDatePicker, FormTextField } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { log } from '@/lib/utils/logger';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey, type OptionMap } from '@/lib/options/optionTypes';
import { useBackendStore } from '@/stores/backendStore';
import {
  AddToInventoryRequest,
  MaterialDto,
  ProjectDto,
  ProjectTypeDto,
  PrivilegeEnum,
  ReportingCategoryDto,
  SampleActivityDto,
  SampleDto,
} from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore } from '@/stores/dialogStore';
import { IdLabelPair } from '@/types/common';
import { KeyValueType } from '@/lib/options/optionTypes';
import { MaterialCreateAddButton } from '@/components/material/MaterialCreateAddButton';
import { MaterialCreateDialog } from '@/components/material/MaterialCreateDialog';
import { useCreateMaterial } from '@/hooks/useCreateMaterial';
import { toNonSubstrateMaterialPicks, toSubstrateMaterialPicks } from '@/lib/materials/materialOptions';
import CustomizingEntityDialog from '@/components/customizing/CustomizingEntityDialog';
import { ENTITY_CONFIGS } from '@/components/customizing/EntityConfigs';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { rankFilteredOptionsByInputMatch } from '@/lib/utils/autocompleteMatchRank';
import {
  filterSubstrateRows,
  pickRepresentativeSubstrateRow,
  uniqueSortedDropdownStrings,
} from '@/lib/utils/substrateLookup';
import {
  createProjectResolver,
  enforceProjectMultiplicitySelection,
  sampleProjectMultiplicityFieldHint,
  validateProjectIdsForSampleMultiplicity,
} from '@/lib/utils/sampleProjectMultiplicity';
import {
  formatAddToInventorySuccessMessage,
  MAX_ADD_TO_INVENTORY_QUANTITY,
  parseAddToInventoryQuantityInput,
  validateAddToInventoryQuantity,
} from '@/lib/addToInventoryBatch';
import {
  buildVariedSpecimens,
  collectSpecimenImages,
  createEmptySpecimenRow,
  hasAnySpecimenImage,
  MAX_VARIED_SPECIMENS,
  nextSpecimenRowId,
  validateVariedRows,
  type SpecimenRowState,
} from '@/lib/addToInventoryVaried';
import { AddToInventorySpecimenRow } from '@/components/dialog/AddToInventorySpecimenRow';
import type { AddToInventoryDialogResult } from '@/types/addToInventory';
import type { AddToInventoryVariedRequest } from '@/api/ApiClient';

const OPTION_KEYS: OptionKey[] = [
  'projects',
  'projectType',
  'substrateDropdownChoices',
  'substrates',
  'materials',
  'researcherOptions',
  'sampleDropdownChoices',
  'sampleReportingCategory',
  'synthesisTechniques',
];

type AddToInventoryDialogOptions = Partial<
  Pick<
    OptionMap,
    | 'projects'
    | 'projectType'
    | 'substrateDropdownChoices'
    | 'substrates'
    | 'materials'
    | 'researcherOptions'
    | 'sampleDropdownChoices'
    | 'sampleReportingCategory'
    | 'synthesisTechniques'
  >
>;

/** Substrate-only inventory vs registering a grown sample (film / product materials on the stack). */
const InventoryKind = {
  SubstrateOnly: 'substrateOnly',
  GrownSample: 'grownSample',
} as const;
type InventoryKind = (typeof InventoryKind)[keyof typeof InventoryKind];

/**
 * Substrate-only registration mode: "Same" copies one set of fields to every specimen (Quantity, Phase 2a);
 * "Customize" lets each specimen have its own substrate metadata and image (Phase 3, varied endpoints).
 */
const RegistrationMode = {
  Same: 'same',
  Customize: 'customize',
} as const;
type RegistrationMode = (typeof RegistrationMode)[keyof typeof RegistrationMode];

/** Internal project types (`ProjectTypeDto.isInternal`) use sample reporting categories in this flow. */
function projectTypeUsesSampleReportingCategory(type: ProjectTypeDto | undefined): boolean {
  return type?.isInternal === true;
}

/**
 * {@link SampleDto} does not embed activities; callers use flags like {@link SampleDto.hasActivities} (e.g. tree expand).
 * After a processing activity is created, the API may return a nested {@link SampleDto} on the activity; merge it and
 * set {@link SampleDto.hasActivities} so consumers match server state.
 */
function sampleAfterOptionalProcessingCreate(
  savedSample: SampleDto,
  createdProcessing: SampleActivityDto | null
): SampleDto {
  if (!createdProcessing) {
    return savedSample;
  }
  const nested = createdProcessing.sample;
  return {
    ...savedSample,
    ...(nested ?? {}),
    id: savedSample.id ?? nested?.id,
    sampleLabel: savedSample.sampleLabel ?? nested?.sampleLabel,
    hasActivities: true,
  };
}

/** Matches MUI `size="small"` outlined input height; parent row uses `alignItems: 'flex-end'` so no manual `mt` offset. */
const ADD_ENTITY_ICON_BUTTON_SX = {
  height: 40,
  width: 40,
  flexShrink: 0,
  backgroundColor: 'primary.main',
  color: 'white',
  border: 1,
  borderColor: 'primary.main',
  borderRadius: 1,
  '&:hover': {
    backgroundColor: 'primary.dark',
    borderColor: 'primary.dark',
  },
  '&:active': {
    backgroundColor: 'primary.dark',
  },
} as const;

interface AddToInventoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful add (resolves `showAddToInventoryDialog`). */
  onInventoryComplete: (data: AddToInventoryDialogResult) => void;
  /** If project is provided, this will initialize the project field*/
  project?: ProjectDto | undefined;
}

interface AddToInventoryDialogInnerProps extends AddToInventoryDialogProps {
  options: AddToInventoryDialogOptions;
}

const AddToInventoryDialogInner: FC<AddToInventoryDialogInnerProps> = ({
  open,
  onClose,
  onInventoryComplete,
  project,
  options,
}) => {
  const backend = useBackendStore((state) => state.getBackend());
  const systemConfig = useSystemConfig();
  const multiplicityPolicy = systemConfig?.sampleProjectMultiplicity;
  /** Under MaxOneProject the picker and autocomplete are single-select; other policies still allow multiple internal projects where the server does. */
  const allowMultiProject = multiplicityPolicy !== 'MaxOneProject';
  const multiplicityHint = sampleProjectMultiplicityFieldHint(multiplicityPolicy);
  const { showProjectSelectionDialog, showProcessingDialog } = useDialogStore();

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedOrientation, setSelectedOrientation] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [selectedLot, setSelectedLot] = useState<string | null>(null);
  const [selectedSubstrateName, setSelectedSubstrateName] = useState<string | null>(null);
  const [selectedThickness, setSelectedThickness] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedHolder, setSelectedHolder] = useState<string | null>(null);
  const [selectedQuality, setSelectedQuality] = useState<string | null>(null);
  // Initialize from project prop - component remounts when key changes (project.id), so initializer runs with new project
  const [selectedProjects, setSelectedProjects] = useState<IdLabelPair[]>(() =>
    project ? [{ id: project.id!, label: project.projectLabel }] : []
  );
  const [selectedMaterials, setSelectedMaterials] = useState<IdLabelPair[]>([]);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [refreshedMaterials, setRefreshedMaterials] = useState<MaterialDto[] | null>(null);
  const [isReportingCategoryDialogOpen, setIsReportingCategoryDialogOpen] = useState(false);
  const [updatedReportingCategories, setUpdatedReportingCategories] = useState<ReportingCategoryDto[] | null>(null);
  const { currentUser, isAuthorized } = useAuthStore();
  const allowCreateWithoutProject = canCreateWithoutProject(isAuthorized);
  const hasCreateSampleForOthers = isAuthorized(PrivilegeEnum.CreateSampleForOthers, 'read');
  // Initialize with default researcher matching current user
  const [selectedResearcher, setSelectedResearcher] = useState<KeyValueType | null>(() => {
    if (currentUser?.id && options.researcherOptions?.length) {
      return options.researcherOptions.find((r) => r.key === currentUser.id) || null;
    }
    return null;
  });
  const [inventoryKind, setInventoryKind] = useState<InventoryKind>(InventoryKind.SubstrateOnly);
  /** Substrate-only: Same (Quantity) vs Customize (per-specimen rows). Always Same for grown samples. */
  const [registrationMode, setRegistrationMode] = useState<RegistrationMode>(RegistrationMode.Same);
  /** Customize-mode rows; one per specimen. Initialized with a single empty row. */
  const [specimenRows, setSpecimenRows] = useState<SpecimenRowState[]>(() => [createEmptySpecimenRow(1)]);
  /** Set on a failed submit so empty per-row material fields show their error. */
  const [specimenSubmitAttempted, setSpecimenSubmitAttempted] = useState(false);
  /** Substrate-only path: optional “planned” technique in the header row. */
  const [plannedTechnique, setPlannedTechnique] = useState<{
    id: string;
    name: string;
    growOnSubstrate: boolean;
  } | null>(null);
  /** Grown-sample path: required growth technique in the grown block (not “planned”). */
  const [grownTechnique, setGrownTechnique] = useState<{
    id: string;
    name: string;
    growOnSubstrate: boolean;
  } | null>(null);
  const [selectedProductMaterials, setSelectedProductMaterials] = useState<IdLabelPair[]>([]);
  const [isProductMaterialDialogOpen, setIsProductMaterialDialogOpen] = useState(false);
  const [growthTechniqueError, setGrowthTechniqueError] = useState(false);
  const [productMaterialError, setProductMaterialError] = useState(false);

  // Track user interaction - once user does anything, always show warning on close
  const { hasUnsavedChanges, markAsInteracted, reset } = useUserInteraction();
  const [selectedReportingCategory, setSelectedReportingCategory] = useState<ReportingCategoryDto | null>(null);
  const [reportingCategoryError, setReportingCategoryError] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [materialError, setMaterialError] = useState(false);
  const [researcherError, setResearcherError] = useState(false);
  const [projectError, setProjectError] = useState(false);
  const [projectMultiplicityMessage, setProjectMultiplicityMessage] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();
  const { canCreateMaterial } = useCreateMaterial();

  const projectOptions = useMemo(
    () =>
      Array.from(
        new Map(
          options.projects
            ?.filter((p) => p.id !== undefined && p.projectLabel !== undefined)
            .map((p) => [p.id!, { id: p.id!, label: p.projectLabel! }])
        ).values()
      ),
    [options.projects]
  );

  const resolveProjectDto = createProjectResolver(options.projects, options.projectType, project);

  /** How many identical specimens to register (phase 1 batch: shared metadata). */
  const [quantityInput, setQuantityInput] = useState('1');
  const [quantityError, setQuantityError] = useState(false);
  const [quantityHelperText, setQuantityHelperText] = useState<string | undefined>(undefined);
  const effectiveQuantity = useMemo(() => parseAddToInventoryQuantityInput(quantityInput), [quantityInput]);
  /** After a successful add, open Add Processing before navigating away (single specimen only). */
  const [startProcessingAfterAdd, setStartProcessingAfterAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // File change handler
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      log.debug('[AddToInventoryDialog] Selected image:', file.name);
      setSelectedFile(file);
      markAsInteracted();
    }
  };

  const handleInventoryKindChange = (_event: React.MouseEvent<HTMLElement>, next: InventoryKind | null) => {
    if (next === null) return;
    setInventoryKind(next);
    markAsInteracted();
    if (next === InventoryKind.SubstrateOnly) {
      setGrownTechnique(null);
      setSelectedProductMaterials([]);
      setGrowthTechniqueError(false);
      setProductMaterialError(false);
    } else {
      setPlannedTechnique(null);
      setGrowthTechniqueError(false);
      setProductMaterialError(false);
      // Customize mode is substrate-only; grown samples always use the single-form Same path.
      setRegistrationMode(RegistrationMode.Same);
    }
  };

  /** Customize mode is offered only for substrate-only inventory. */
  const isCustomizeMode =
    inventoryKind === InventoryKind.SubstrateOnly && registrationMode === RegistrationMode.Customize;

  const handleRegistrationModeChange = (_event: React.MouseEvent<HTMLElement>, next: RegistrationMode | null) => {
    if (next === null) return;
    setRegistrationMode(next);
    setSpecimenSubmitAttempted(false);
    markAsInteracted();
  };

  /** Patch a subset of one specimen row's fields. */
  const handleSpecimenRowChange = useCallback((id: number, patch: Partial<SpecimenRowState>) => {
    setSpecimenRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const handleAddSpecimenRow = useCallback(() => {
    setSpecimenRows((rows) =>
      rows.length >= MAX_VARIED_SPECIMENS ? rows : [...rows, createEmptySpecimenRow(nextSpecimenRowId(rows))]
    );
    markAsInteracted();
  }, [markAsInteracted]);

  const handleRemoveSpecimenRow = useCallback(
    (id: number) => {
      // Keep at least one row so the section never collapses to empty.
      setSpecimenRows((rows) => (rows.length <= 1 ? rows : rows.filter((row) => row.id !== id)));
      markAsInteracted();
    },
    [markAsInteracted]
  );

  const handleReportingCategoryCreate = async (categoryData: any) => {
    // Frontend validation: Check for duplicate code
    const code = categoryData.code?.trim();
    if (!code) {
      showSnackbar('Code is required', 'error');
      return;
    }

    // Check against existing reporting categories (from options and updated state)
    const allCategories = updatedReportingCategories ?? options.sampleReportingCategory ?? [];
    const duplicateCategory = allCategories.find((cat) => cat.code?.toLowerCase() === code.toLowerCase());

    if (duplicateCategory) {
      showSnackbar(`Reporting category with code '${code}' already exists`, 'error');
      return;
    }

    try {
      // First, save the reporting category to the backend
      const savedCategory = await backend.sample.createReportingCategory(categoryData);

      // Close the dialog immediately
      setIsReportingCategoryDialogOpen(false);

      // Refresh the reporting categories options by fetching from backend
      const freshCategories = await backend.sample.listReportingCategories(false);

      // Update the reporting categories state so autocomplete shows the new category
      setUpdatedReportingCategories(freshCategories);

      // Auto-select the newly created category
      if (savedCategory) {
        setSelectedReportingCategory(savedCategory);
        setReportingCategoryError(false);
      }

      showSnackbar('New reporting category created successfully', 'success');
    } catch (error: any) {
      log.error('Error creating reporting category:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create new reporting category', 'error');
    }
  };

  const reportingCategoryOptions = updatedReportingCategories ?? options.sampleReportingCategory ?? [];
  const hasReportingCategoriesInSystem = reportingCategoryOptions.length > 0;

  const selectedProjectsNeedReportingCategory = useMemo(() => {
    const resolve = createProjectResolver(options.projects, options.projectType, project);
    return (
      selectedProjects.length > 0 &&
      selectedProjects.some((sp) => projectTypeUsesSampleReportingCategory(resolve(sp.id)?.type))
    );
  }, [selectedProjects, options.projects, options.projectType, project]);

  /** At least one selected project has an internal type and reporting categories exist: show field and require a choice. */
  const showReportingCategoryField = hasReportingCategoriesInSystem && selectedProjectsNeedReportingCategory;

  const clearReportingCategoryIfHidden = useCallback(
    (nextProjects: IdLabelPair[]) => {
      const resolve = createProjectResolver(options.projects, options.projectType, project);
      const needs =
        nextProjects.length > 0 &&
        nextProjects.some((sp) => projectTypeUsesSampleReportingCategory(resolve(sp.id)?.type));
      const listLen = (updatedReportingCategories ?? options.sampleReportingCategory ?? []).length;
      if (!needs || listLen === 0) {
        setSelectedReportingCategory(null);
        setReportingCategoryError(false);
      }
    },
    [options.projects, options.projectType, project, updatedReportingCategories, options.sampleReportingCategory]
  );

  const handleProjectLookup = useCallback(async () => {
    const projects = await showProjectSelectionDialog(allowMultiProject ? 'Select Projects' : 'Select Project', {
      allowMultiple: allowMultiProject,
    });
    if (projects) {
      const newProjects = projects.map((p) => ({ id: p.id!, label: p.projectLabel! }));
      const applied = allowMultiProject ? newProjects : newProjects.slice(0, 1);
      const resolve = createProjectResolver(options.projects, options.projectType, project);
      const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
        multiplicityPolicy,
        applied,
        resolve
      );
      setSelectedProjects(nextProjects);
      if (nextProjects.length > 0) setProjectError(false);
      setProjectMultiplicityMessage(multiplicityMessage);
      clearReportingCategoryIfHidden(nextProjects);
      markAsInteracted();
    }
  }, [
    allowMultiProject,
    multiplicityPolicy,
    options.projects,
    options.projectType,
    project,
    showProjectSelectionDialog,
    clearReportingCategoryIfHidden,
    markAsInteracted,
  ]);

  const sd = options.substrateDropdownChoices;
  const substrateRows = options.substrates;

  const substrateNameOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.names), [sd?.names]);

  const substrateVendorOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.vendors), [sd?.vendors]);

  const substrateLotOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.lots), [sd?.lots]);

  const substrateOrientationOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.orientations), [sd?.orientations]);

  const techniqueOptions = useMemo(
    () =>
      options.synthesisTechniques
        ?.filter((t) => t.id !== undefined && t.name !== undefined && t.name !== 'None')
        .sort((a, b) => a.name!.localeCompare(b.name!))
        .map((t) => ({
          id: t.id!,
          name: t.name!,
          growOnSubstrate: t.growOnSubstrate ?? true,
        })) ?? [],
    [options.synthesisTechniques]
  );

  /**
   * Same rule as SampleDetails for substrate-only: no planned technique → show substrate section.
   * Grown-sample: substrate section only after a growth technique is chosen and it grows on a substrate.
   */
  const techniqueUsesSubstrates = useMemo(() => {
    if (inventoryKind === InventoryKind.GrownSample) {
      if (grownTechnique === null) return false;
      return grownTechnique.growOnSubstrate ?? true;
    }
    return plannedTechnique === null ? true : (plannedTechnique.growOnSubstrate ?? true);
  }, [inventoryKind, plannedTechnique, grownTechnique]);

  /** When a technique is chosen that does not use substrates, explain why the substrate block is hidden. */
  const showNonSubstrateTechniqueMessage =
    !techniqueUsesSubstrates &&
    (inventoryKind === InventoryKind.GrownSample ? grownTechnique != null : plannedTechnique != null);

  /** Dropped substrate UI must not leave stale values in state; clear in the technique handler. */
  const clearSubstrateOnlyInventoryFields = () => {
    setSelectedSubstrateName(null);
    setSelectedOrientation(null);
    setSelectedVendor(null);
    setSelectedLot(null);
    setSelectedThickness(null);
    setSelectedSize(null);
    setSelectedHolder(null);
    setSelectedQuality(null);
    setSelectedMaterials([]);
    setMaterialError(false);
  };

  // When user cannot create samples for others, restrict researcher to current user only.
  const researcherOptions = useMemo(() => {
    const list = options.researcherOptions ?? [];
    if (hasCreateSampleForOthers) return list;
    if (!currentUser?.id) return [];
    const match = list.find((r) => String(r.key) === String(currentUser.id));
    return match ? [match] : [];
  }, [options.researcherOptions, hasCreateSampleForOthers, currentUser]);

  const currentUserNotInResearcherList =
    !hasCreateSampleForOthers && currentUser?.id != null && options.researcherOptions?.length !== undefined
      ? !options.researcherOptions.some((r) => String(r.key) === String(currentUser.id))
      : false;

  const sourceMaterials = useMemo(
    () => refreshedMaterials ?? options.materials ?? [],
    [refreshedMaterials, options.materials]
  );

  const substrateMaterialOptions = useMemo(() => toSubstrateMaterialPicks(sourceMaterials), [sourceMaterials]);

  const productMaterialOptions = useMemo(() => toNonSubstrateMaterialPicks(sourceMaterials), [sourceMaterials]);

  const handleSubstrateNameCommitted = useCallback(
    (newValue: string | null, reason: string) => {
      const name = newValue ?? null;
      setSelectedSubstrateName(name);
      if (reason !== 'selectOption' || !name || !substrateRows?.length) return;
      const matches = filterSubstrateRows(substrateRows, { substrateName: name });
      const row = pickRepresentativeSubstrateRow(matches);
      if (!row) return;
      setSelectedVendor(row.substrateVendor ?? null);
      setSelectedLot(row.substrateLot ?? null);
      setSelectedOrientation(row.substrateOrientation ?? null);
      const mid = row.substrateMaterialIds?.[0];
      if (mid != null) {
        const pair = substrateMaterialOptions.find((m) => m.id === mid);
        if (pair) setSelectedMaterials([pair]);
      }
    },
    [substrateRows, substrateMaterialOptions]
  );

  const materialAutocompleteFilterOptions = useMemo(() => {
    const base = createFilterOptions<IdLabelPair>({ stringify: (option) => option.label });
    return (options: readonly IdLabelPair[], state: FilterOptionsState<IdLabelPair>) => {
      const filtered = base([...options], state);
      return rankFilteredOptionsByInputMatch(filtered, state.inputValue, (o) => o.label);
    };
  }, []);

  // refreshedMaterials resets automatically when dialog remounts via key prop

  /** Clear form fields without closing the dialog (`onInventoryComplete` closes and resolves the awaiter). */
  const resetInventoryFormFields = () => {
    setSelectedDate(new Date());
    setSelectedSubstrateName(null);
    setSelectedOrientation(null);
    setSelectedVendor(null);
    setSelectedLot(null);
    setSelectedProjects([]);
    setProjectMultiplicityMessage(null);
    setSelectedMaterials([]);
    setSelectedResearcher(
      currentUser?.id && options.researcherOptions?.length
        ? (options.researcherOptions.find((r) => r.key === currentUser.id) ?? null)
        : null
    );
    setInventoryKind(InventoryKind.SubstrateOnly);
    setRegistrationMode(RegistrationMode.Same);
    setSpecimenRows([createEmptySpecimenRow(1)]);
    setSpecimenSubmitAttempted(false);
    setPlannedTechnique(null);
    setGrownTechnique(null);
    setSelectedProductMaterials([]);
    setGrowthTechniqueError(false);
    setProductMaterialError(false);
    setSelectedReportingCategory(null);
    setReportingCategoryError(false);
    setDescription('');
    setSelectedThickness(null);
    setSelectedSize(null);
    setSelectedHolder(null);
    setSelectedQuality(null);
    setSelectedFile(null);
    setUpdatedReportingCategories(null);
    setStartProcessingAfterAdd(false);
    setQuantityInput('1');
    setQuantityError(false);
    setQuantityHelperText(undefined);
    reset(); // Reset user interaction state
  };

  const handleFormReset = () => {
    resetInventoryFormFields();
    onClose();
  };

  // handle submit functions
  const handleSubmit = async () => {
    let hasError = false;

    if (!selectedResearcher) {
      setResearcherError(true);
      hasError = true;
    } else if (!hasCreateSampleForOthers && String(selectedResearcher.key) !== String(currentUser?.id)) {
      setResearcherError(true);
      hasError = true;
    } else {
      setResearcherError(false);
    }

    if (inventoryKind === InventoryKind.GrownSample) {
      if (!grownTechnique) {
        setGrowthTechniqueError(true);
        hasError = true;
      } else {
        setGrowthTechniqueError(false);
      }
      if (selectedProductMaterials.length === 0) {
        setProductMaterialError(true);
        hasError = true;
      } else {
        setProductMaterialError(false);
      }
    } else {
      setGrowthTechniqueError(false);
      setProductMaterialError(false);
    }

    // Same-mode uses the single shared Material field; customize mode validates per-row materials below.
    if (!isCustomizeMode && techniqueUsesSubstrates && selectedMaterials.length === 0) {
      setMaterialError(true);
      hasError = true;
    } else {
      setMaterialError(false);
    }

    if (!allowCreateWithoutProject && selectedProjects.length === 0) {
      setProjectError(true);
      hasError = true;
    } else {
      setProjectError(false);
    }

    const multiplicityError = validateProjectIdsForSampleMultiplicity(
      multiplicityPolicy,
      selectedProjects.map((p) => p.id),
      resolveProjectDto
    );
    if (multiplicityError) {
      showSnackbar(multiplicityError, 'error');
      return;
    }

    if (showReportingCategoryField && !selectedReportingCategory) {
      setReportingCategoryError(true);
      hasError = true;
    } else {
      setReportingCategoryError(false);
    }

    // Quantity only applies to Same mode; customize mode registers one sample per row instead.
    let customizeRowsError: string | null = null;
    if (isCustomizeMode) {
      customizeRowsError = validateVariedRows(specimenRows);
      if (customizeRowsError) {
        setSpecimenSubmitAttempted(true);
        hasError = true;
      }
    } else {
      const quantityValidationMessage = validateAddToInventoryQuantity(effectiveQuantity);
      if (quantityValidationMessage) {
        setQuantityError(true);
        setQuantityHelperText(quantityValidationMessage);
        hasError = true;
      } else {
        setQuantityError(false);
        setQuantityHelperText(`Up to ${MAX_ADD_TO_INVENTORY_QUANTITY} identical specimens.`);
      }
    }

    if (hasError) {
      showSnackbar(
        !hasCreateSampleForOthers && selectedResearcher && String(selectedResearcher.key) !== String(currentUser?.id)
          ? 'You can only create samples for yourself.'
          : (customizeRowsError ?? 'Please fill out all required fields.'),
        'error'
      );
      return;
    }

    // Customize mode: build one varied request and post it (multipart only when a row carries an image).
    if (isCustomizeMode) {
      setIsSubmitting(true);
      try {
        const variedRequest: AddToInventoryVariedRequest = {
          date: selectedDate ?? new Date(),
          projectIds: selectedProjects.map((p) => p.id),
          researcherKey: selectedResearcher!.key,
          reportingCategory:
            showReportingCategoryField && selectedReportingCategory ? selectedReportingCategory : undefined,
          description: description || '',
          techniqueId: plannedTechnique?.id || undefined,
          specimens: buildVariedSpecimens(specimenRows),
        };

        let variedResponse;
        if (hasAnySpecimenImage(specimenRows)) {
          const { files, indexes } = collectSpecimenImages(specimenRows);
          const fileParams = files.map((file) => ({ data: file, fileName: file.name }));
          variedResponse = await backend.sample.addToInventoryVariedWithImages(
            JSON.stringify(variedRequest),
            fileParams,
            indexes
          );
        } else {
          variedResponse = await backend.sample.addToInventoryVaried(variedRequest);
        }

        const savedSamples = variedResponse.samples ?? [];
        if (savedSamples.length === 0) {
          throw new Error('Add to inventory did not return a created sample.');
        }

        log.debug('[AddToInventoryDialog] Saved varied samples:', savedSamples);
        showSnackbar(formatAddToInventorySuccessMessage(savedSamples, 'substrate'), 'success');

        resetInventoryFormFields();
        // Callers navigate only when exactly one sample is returned; multiple just refresh the grid.
        onInventoryComplete({ samples: savedSamples });
        return;
      } catch (error: unknown) {
        showSnackbar(getProblemDetail(error) ?? 'Failed to add samples to inventory.', 'error');
        handleFormReset();
        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    const submitQuantity = effectiveQuantity;

    setIsSubmitting(true);
    try {
      const techniqueIdForPayload =
        inventoryKind === InventoryKind.GrownSample ? grownTechnique?.id : plannedTechnique?.id;
      const productIdsForPayload =
        inventoryKind === InventoryKind.GrownSample ? selectedProductMaterials.map((m) => m.id) : undefined;

      const payload: AddToInventoryRequest = {
        label: undefined,
        quantity: submitQuantity,
        substrateName: techniqueUsesSubstrates ? selectedSubstrateName || undefined : undefined,
        substrateVendor: techniqueUsesSubstrates ? selectedVendor || '' : '',
        substrateLot: techniqueUsesSubstrates ? selectedLot || '' : '',
        thickness: techniqueUsesSubstrates ? selectedThickness || '' : '',
        size: techniqueUsesSubstrates ? selectedSize || '' : '',
        sampleHolder: techniqueUsesSubstrates ? selectedHolder || '' : '',
        substrateOrientation: techniqueUsesSubstrates ? selectedOrientation || '' : '',
        quality: techniqueUsesSubstrates ? selectedQuality || '' : '',
        date: selectedDate ?? new Date(),
        projectIds: selectedProjects.map((p) => p.id),
        substrateMaterialIds: techniqueUsesSubstrates ? selectedMaterials.map((m) => m.id) : [],
        productMaterialIds: productIdsForPayload,
        researcherKey: selectedResearcher!.key,
        reportingCategory:
          showReportingCategoryField && selectedReportingCategory ? selectedReportingCategory : undefined,
        description: description || '',
        techniqueId: techniqueIdForPayload || undefined,
      };

      let createResponse;

      if (selectedFile) {
        const fileParam = {
          data: selectedFile,
          fileName: selectedFile.name,
        };

        const sampleJson = JSON.stringify(payload);
        createResponse = await backend.sample.addToInventoryWithImage(fileParam, sampleJson);
      } else {
        createResponse = await backend.sample.addToInventory(payload);
      }

      const savedSamples = createResponse.samples ?? [];
      const savedSample = savedSamples[0];
      if (!savedSample || savedSamples.length === 0) {
        throw new Error('Add to inventory did not return a created sample.');
      }

      log.debug('[AddToInventoryDialog] Saved samples:', savedSamples);

      showSnackbar(
        formatAddToInventorySuccessMessage(
          savedSamples,
          inventoryKind === InventoryKind.GrownSample ? 'grown' : 'substrate'
        ),
        'success'
      );

      const techniqueForProcessing = inventoryKind === InventoryKind.GrownSample ? grownTechnique : plannedTechnique;
      const processingPrefill =
        startProcessingAfterAdd && submitQuantity === 1 && savedSample.id != null
          ? {
              date: selectedDate ?? new Date(),
              technique: techniqueForProcessing,
              description: description || '',
              researcherUserId: String(selectedResearcher!.key),
            }
          : null;

      resetInventoryFormFields();

      // `onInventoryComplete` resolves `showAddToInventoryDialog`; callers (e.g. search panels) navigate on resolve.
      // Run optional Add Processing first so navigation does not interrupt this dialog.
      let sampleToResolve = savedSample;
      if (processingPrefill) {
        const createdProcessing = await showProcessingDialog(savedSample, processingPrefill);
        sampleToResolve = sampleAfterOptionalProcessingCreate(savedSample, createdProcessing);
      }

      onInventoryComplete({ samples: submitQuantity === 1 ? [sampleToResolve] : savedSamples });
      return;
    } catch (error: unknown) {
      // Reuse shared API error handling so auth failures (403) show a meaningful message
      // instead of a generic "unexpected server error" string.
      showSnackbar(getProblemDetail(error) ?? 'Failed to add sample to inventory.', 'error');
      handleFormReset();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StandardDialog
        open={open}
        onClose={handleFormReset}
        title="Add to Inventory"
        maxWidth="lg"
        onSubmit={handleSubmit}
        submitText={
          isCustomizeMode
            ? specimenRows.length > 1
              ? `Add ${specimenRows.length}`
              : 'Add'
            : effectiveQuantity > 1
              ? `Add ${effectiveQuantity}`
              : 'Add'
        }
        helpDocKey="dialog.addToInventory"
        isLoading={isSubmitting}
        loadingText="Adding…"
        hasUnsavedChanges={hasUnsavedChanges}
        unsavedChangesMessage="You have unsaved changes to this inventory item. Are you sure you want to discard them?"
        secondaryActions={
          <FormControlLabel
            sx={{ ml: 0, mr: 0, my: 0, alignItems: 'center' }}
            control={
              <Checkbox
                // Start-processing applies only to a single specimen; disabled for batch Quantity and customize mode.
                checked={!isCustomizeMode && effectiveQuantity === 1 ? startProcessingAfterAdd : false}
                disabled={isCustomizeMode || effectiveQuantity > 1}
                onChange={(_, checked) => {
                  setStartProcessingAfterAdd(checked);
                  markAsInteracted();
                }}
                size="small"
              />
            }
            label={
              <Typography
                variant="body2"
                component="span"
                color={isCustomizeMode || effectiveQuantity > 1 ? 'text.disabled' : 'text.primary'}
              >
                {isCustomizeMode || effectiveQuantity > 1
                  ? 'Start processing after adding (single specimen only)'
                  : 'Start processing after adding'}
              </Typography>
            }
          />
        }
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* Project / Added By / Date + technique row — vertical rhythm matches substrate inner grids (gap 1.5). */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  // Project needs more track than "Added By": EntityAutocompleteWithLookup must use fullWidth to fill it.
                  // Customize mode hides Quantity (one sample per row instead), so the grid drops that column.
                  md: isCustomizeMode
                    ? 'minmax(0, 2fr) minmax(0, 1fr) minmax(160px, 200px)'
                    : 'minmax(0, 2fr) minmax(0, 1fr) minmax(88px, 100px) minmax(160px, 200px)',
                },
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <EntityAutocompleteWithLookup
                  label={`${allowMultiProject ? 'Project(s)' : 'Project'}${allowCreateWithoutProject ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={(newValue) => {
                    // Policy can make it a single select field, or a multi-select field.
                    // We need to handle both cases.
                    const incomingProjects = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
                    const { nextProjects, multiplicityMessage } = enforceProjectMultiplicitySelection(
                      multiplicityPolicy,
                      incomingProjects,
                      resolveProjectDto
                    );
                    setSelectedProjects(nextProjects);
                    if (nextProjects.length > 0) setProjectError(false);
                    setProjectMultiplicityMessage(multiplicityMessage);
                    clearReportingCategoryIfHidden(nextProjects);
                    markAsInteracted();
                  }}
                  onLookupClick={handleProjectLookup}
                  multiple={allowMultiProject}
                  lookupTooltip={allowMultiProject ? 'Search and select projects' : 'Search and select a project'}
                  error={projectError}
                  helperText={
                    projectError
                      ? 'At least one project is required.'
                      : (projectMultiplicityMessage ?? multiplicityHint ?? undefined)
                  }
                  fullWidth
                  size="small"
                />
              </Box>
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <FormAutocomplete
                  options={researcherOptions}
                  getOptionLabel={(option) => option.value ?? ''}
                  renderOption={(props, option) => (
                    <li {...props} key={option.key}>
                      {option.value}
                    </li>
                  )}
                  value={selectedResearcher}
                  onChange={(_, newValue) => {
                    setSelectedResearcher(newValue || null);
                    if (newValue?.key) setResearcherError(false);
                    markAsInteracted();
                  }}
                  label="Added By"
                  required
                  error={researcherError}
                  helperText={
                    researcherError
                      ? 'Researcher is required.'
                      : currentUserNotInResearcherList
                        ? 'Current user is not in the researcher list; contact an administrator.'
                        : !hasCreateSampleForOthers
                          ? 'Only yourself can be selected.'
                          : undefined
                  }
                  autoSelect
                  autoHighlight
                  size="small"
                />
              </Box>
              {!isCustomizeMode ? (
                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <FormTextField
                    label="Quantity"
                    type="number"
                    value={quantityInput}
                    onChange={(e) => {
                      const nextQuantity = parseAddToInventoryQuantityInput(e.target.value);
                      setQuantityInput(e.target.value);
                      if (nextQuantity > 1) {
                        setStartProcessingAfterAdd(false);
                      }
                      setQuantityError(false);
                      setQuantityHelperText(`Up to ${MAX_ADD_TO_INVENTORY_QUANTITY} identical specimens.`);
                      markAsInteracted();
                    }}
                    inputProps={{
                      min: 1,
                      max: MAX_ADD_TO_INVENTORY_QUANTITY,
                      step: 1,
                    }}
                    error={quantityError}
                    helperText={quantityHelperText ?? `Up to ${MAX_ADD_TO_INVENTORY_QUANTITY} identical specimens.`}
                    size="small"
                    fullWidth
                  />
                </Box>
              ) : null}
              <Box sx={{ width: { xs: '100%', md: '100%' }, minWidth: 0 }}>
                <FormDatePicker
                  label="Date"
                  value={selectedDate}
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                    markAsInteracted();
                  }}
                  required
                  size="small"
                />
              </Box>
            </Box>
            <ToggleButtonGroup
              exclusive
              value={inventoryKind}
              onChange={handleInventoryKindChange}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                '& .MuiToggleButton-root': { px: 2, py: 0.75, textTransform: 'none' },
              }}
            >
              <ToggleButton value={InventoryKind.SubstrateOnly}>Substrate inventory</ToggleButton>
              <ToggleButton value={InventoryKind.GrownSample}>Grown sample</ToggleButton>
            </ToggleButtonGroup>
            {/* Registration mode: substrate-only. Same = identical specimens (Quantity); Customize = per-row fields. */}
            {inventoryKind === InventoryKind.SubstrateOnly ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <ToggleButtonGroup
                  exclusive
                  value={registrationMode}
                  onChange={handleRegistrationModeChange}
                  size="small"
                  sx={{
                    alignSelf: 'flex-start',
                    '& .MuiToggleButton-root': { px: 2, py: 0.75, textTransform: 'none' },
                  }}
                >
                  <ToggleButton value={RegistrationMode.Same}>Same details for all</ToggleButton>
                  <ToggleButton value={RegistrationMode.Customize}>Customize each</ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary">
                  {isCustomizeMode
                    ? 'Each row becomes its own sample ID. Shared project, date, and researcher apply to all.'
                    : 'One set of fields is copied to every specimen.'}
                </Typography>
              </Box>
            ) : null}
            {inventoryKind === InventoryKind.SubstrateOnly || showReportingCategoryField ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md:
                      inventoryKind === InventoryKind.SubstrateOnly && showReportingCategoryField
                        ? 'minmax(0, 1fr) minmax(0, 1fr)'
                        : '1fr',
                  },
                  gap: 1.5,
                  alignItems: 'start',
                }}
              >
                {inventoryKind === InventoryKind.SubstrateOnly ? (
                  <FormAutocomplete
                    options={techniqueOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={plannedTechnique}
                    onChange={(_, newValue) => {
                      const next = newValue || null;
                      setPlannedTechnique(next);
                      const usesSubstrates = next === null ? true : (next.growOnSubstrate ?? true);
                      if (!usesSubstrates) {
                        clearSubstrateOnlyInventoryFields();
                      }
                      markAsInteracted();
                    }}
                    label="Planned Growth Technique"
                    autoSelect
                    autoHighlight
                    size="small"
                    fullWidth
                  />
                ) : null}
                {showReportingCategoryField ? (
                  <Box display="flex" gap={1} alignItems="flex-end" minWidth={0} sx={{ width: '100%' }}>
                    <FormAutocomplete
                      options={reportingCategoryOptions}
                      getOptionLabel={(option) => option.label || option.code || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={selectedReportingCategory}
                      onChange={(_, newValue) => {
                        setSelectedReportingCategory(newValue);
                        if (newValue) setReportingCategoryError(false);
                        markAsInteracted();
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.label || option.code}
                        </li>
                      )}
                      label="Reporting Category"
                      required
                      error={reportingCategoryError}
                      helperText={
                        reportingCategoryError
                          ? 'Reporting category is required when an internal project type is selected.'
                          : undefined
                      }
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <IconButton
                      onClick={() => setIsReportingCategoryDialogOpen(true)}
                      size="small"
                      sx={ADD_ENTITY_ICON_BUTTON_SX}
                      title="Add new reporting category"
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            {inventoryKind === InventoryKind.GrownSample ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Grown sample
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                    alignItems: 'start',
                  }}
                >
                  <FormAutocomplete
                    options={techniqueOptions}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={grownTechnique}
                    onChange={(_, newValue) => {
                      const next = newValue || null;
                      setGrownTechnique(next);
                      const usesSubstrates = next === null ? false : (next.growOnSubstrate ?? true);
                      if (!usesSubstrates) {
                        clearSubstrateOnlyInventoryFields();
                      }
                      if (next) setGrowthTechniqueError(false);
                      markAsInteracted();
                    }}
                    label="Growth Technique"
                    required
                    error={growthTechniqueError}
                    helperText={growthTechniqueError ? 'Growth technique is required.' : undefined}
                    autoSelect
                    autoHighlight
                    size="small"
                    fullWidth
                  />
                  <Box display="flex" gap={1} alignItems="flex-end" minWidth={0}>
                    <FormAutocomplete
                      multiple
                      options={productMaterialOptions ?? []}
                      filterOptions={materialAutocompleteFilterOptions}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={selectedProductMaterials}
                      onChange={(_, newValue) => {
                        const next = (newValue as IdLabelPair[] | null) ?? [];
                        setSelectedProductMaterials(next);
                        if (next.length > 0) setProductMaterialError(false);
                        markAsInteracted();
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.label}
                        </li>
                      )}
                      label="Sample (film) materials"
                      required
                      error={productMaterialError}
                      helperText={
                        productMaterialError ? 'At least one film / product material is required.' : undefined
                      }
                      size="small"
                      sx={{ flex: 1, minWidth: 0 }}
                      disableCloseOnSelect
                    />
                    {canCreateMaterial ? (
                      <MaterialCreateAddButton
                        onClick={() => setIsProductMaterialDialogOpen(true)}
                        title="Add new sample (film) material"
                      />
                    ) : null}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>

          {/* Customize mode: a section of per-specimen rows replaces the single shared Substrate block. */}
          {isCustomizeMode && techniqueUsesSubstrates ? (
            <Box sx={{ mb: 1.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Specimens
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddSpecimenRow}
                  disabled={specimenRows.length >= MAX_VARIED_SPECIMENS}
                >
                  Add specimen
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {specimenRows.map((row, index) => (
                  <AddToInventorySpecimenRow
                    key={row.id}
                    index={index}
                    row={row}
                    onChange={handleSpecimenRowChange}
                    onRemove={handleRemoveSpecimenRow}
                    canRemove={specimenRows.length > 1}
                    materialError={specimenSubmitAttempted ? row.materialIds.length === 0 : false}
                    materialOptions={substrateMaterialOptions ?? []}
                    substrateNameOptions={substrateNameOptions}
                    vendorOptions={substrateVendorOptions}
                    lotOptions={substrateLotOptions}
                    orientationOptions={substrateOrientationOptions}
                    sizeOptions={options.sampleDropdownChoices?.sizes ?? []}
                    thicknessOptions={options.sampleDropdownChoices?.thicknesses ?? []}
                    holderOptions={options.sampleDropdownChoices?.sampleHolders ?? []}
                    qualityOptions={options.sampleDropdownChoices?.quality ?? []}
                    onInteract={markAsInteracted}
                  />
                ))}
              </Box>
            </Box>
          ) : null}

          {!isCustomizeMode && techniqueUsesSubstrates ? (
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Substrate
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                <FormAutocomplete
                  freeSolo
                  options={substrateNameOptions}
                  value={selectedSubstrateName}
                  onChange={(_, newValue, reason) => {
                    const v = typeof newValue === 'string' ? newValue : null;
                    handleSubstrateNameCommitted(v, reason);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => {
                    setSelectedSubstrateName(newInputValue || null);
                    markAsInteracted();
                  }}
                  label="Substrate name"
                  size="small"
                  fullWidth
                />
              </Box>
              {/* Lot / vendor */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                  mb: 1.5,
                  alignItems: 'start',
                }}
              >
                <FormAutocomplete
                  freeSolo
                  options={substrateLotOptions.filter((option) => option && option.trim() !== '')}
                  value={selectedLot}
                  onChange={(_, newValue) => {
                    setSelectedLot(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedLot(newInputValue || null)}
                  label="Lot"
                  size="small"
                  fullWidth
                />
                <FormAutocomplete
                  freeSolo
                  options={substrateVendorOptions}
                  value={selectedVendor}
                  onChange={(_, newValue) => {
                    setSelectedVendor(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedVendor(newInputValue || null)}
                  label="Vendor"
                  size="small"
                  fullWidth
                />
              </Box>
              {/* Material (+ add), orientation */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1.5,
                  mb: 1.5,
                  alignItems: 'start',
                }}
              >
                <Box display="flex" gap={1} alignItems="flex-end" minWidth={0}>
                  <FormAutocomplete
                    options={substrateMaterialOptions ?? []}
                    filterOptions={materialAutocompleteFilterOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={selectedMaterials[0] ?? null}
                    onChange={(_, newValue) => {
                      setSelectedMaterials(newValue ? [newValue] : []);
                      markAsInteracted();
                    }}
                    autoSelect
                    autoHighlight
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    label="Material"
                    required
                    error={materialError}
                    size="small"
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  {canCreateMaterial ? <MaterialCreateAddButton onClick={() => setIsMaterialDialogOpen(true)} /> : null}
                </Box>
                <FormAutocomplete
                  freeSolo
                  options={substrateOrientationOptions.filter((option) => option && option.trim() !== '')}
                  value={selectedOrientation}
                  onChange={(_, newValue) => {
                    setSelectedOrientation(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedOrientation(newInputValue || null)}
                  label="Orientation"
                  size="small"
                  fullWidth
                />
              </Box>
              {/* Size, thickness, holder, quality */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                  mb: 1.5,
                  alignItems: 'start',
                }}
              >
                <FormAutocomplete
                  freeSolo
                  options={(options.sampleDropdownChoices?.sizes ?? []).filter(
                    (option) => option && option.trim() !== ''
                  )}
                  value={selectedSize}
                  onChange={(_, newValue) => {
                    setSelectedSize(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedSize(newInputValue)}
                  label="Size"
                  size="small"
                  fullWidth
                />
                <FormAutocomplete
                  freeSolo
                  options={(options.sampleDropdownChoices?.thicknesses ?? []).filter(
                    (option) => option && option.trim() !== ''
                  )}
                  value={selectedThickness}
                  onChange={(_, newValue) => {
                    setSelectedThickness(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedThickness(newInputValue)}
                  label="Thickness"
                  size="small"
                  fullWidth
                />
                <FormAutocomplete
                  freeSolo
                  options={(options.sampleDropdownChoices?.sampleHolders ?? []).filter(
                    (option) => option && option.trim() !== ''
                  )}
                  value={selectedHolder}
                  onChange={(_, newValue) => {
                    setSelectedHolder(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedHolder(newInputValue)}
                  label="Holder"
                  size="small"
                  fullWidth
                />
                <FormAutocomplete
                  freeSolo
                  options={(options.sampleDropdownChoices?.quality ?? []).filter(
                    (option: string) => option && option.trim() !== ''
                  )}
                  value={selectedQuality}
                  onChange={(_, newValue) => {
                    setSelectedQuality(newValue || null);
                    markAsInteracted();
                  }}
                  onInputChange={(_, newInputValue) => setSelectedQuality(newInputValue)}
                  label="Quality"
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          ) : showNonSubstrateTechniqueMessage ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This growth technique does not grow on a substrate, so substrate details are not used.
            </Typography>
          ) : null}

          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'grid',
                // Customize mode hides the single shared image (each specimen has its own), so Notes fills the row.
                gridTemplateColumns: {
                  xs: '1fr',
                  md: isCustomizeMode ? '1fr' : 'minmax(0, 1fr) minmax(160px, 220px)',
                },
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Notes
                </Typography>
                <RichTextEditor
                  value={description}
                  onChange={(value) => {
                    setDescription(value);
                    markAsInteracted();
                  }}
                />
              </Box>
              {!isCustomizeMode ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Typography component="div" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <FormLabel optional>Image</FormLabel>
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    component="label"
                    sx={{ alignSelf: 'flex-start', whiteSpace: 'nowrap' }}
                  >
                    Attach Image
                    <input type="file" hidden accept="image/*" onChange={handleFileChange} />
                  </Button>
                  {selectedFile ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                        width: '100%',
                        mt: 0.5,
                      }}
                    >
                      <Box display="flex" gap={1} alignItems="center" mb={1}>
                        <Box
                          sx={{
                            position: 'relative',
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0,
                          }}
                        >
                          <Image
                            src={URL.createObjectURL(selectedFile)}
                            alt="Preview"
                            layout="fill"
                            objectFit="cover"
                            unoptimized
                          />
                        </Box>
                        <Typography variant="caption" sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
                          {selectedFile.name}
                        </Typography>
                      </Box>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Button
                          size="small"
                          variant="outlined"
                          component="a"
                          href={URL.createObjectURL(selectedFile)}
                          download={selectedFile.name}
                        >
                          Download
                        </Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => setSelectedFile(null)}>
                          <DeleteIcon />
                        </Button>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          </Box>
        </LocalizationProvider>
      </StandardDialog>

      <MaterialCreateDialog
        open={isMaterialDialogOpen}
        forSubstrate
        onClose={() => setIsMaterialDialogOpen(false)}
        onCreated={(savedMaterial, freshMaterials) => {
          setRefreshedMaterials(freshMaterials);
          const createdMaterial = toSubstrateMaterialPicks(freshMaterials).find((m) => m.id === savedMaterial.id);
          if (createdMaterial) {
            setSelectedMaterials([createdMaterial]);
          }
          showSnackbar('New material created successfully', 'success');
        }}
      />

      <MaterialCreateDialog
        open={isProductMaterialDialogOpen}
        forSubstrate={false}
        onClose={() => setIsProductMaterialDialogOpen(false)}
        onCreated={(savedMaterial, freshMaterials) => {
          setRefreshedMaterials(freshMaterials);
          const createdMaterial = toNonSubstrateMaterialPicks(freshMaterials).find((m) => m.id === savedMaterial.id);
          if (createdMaterial) {
            setSelectedProductMaterials((prev) =>
              prev.some((p) => p.id === createdMaterial.id) ? prev : [...prev, createdMaterial]
            );
            setProductMaterialError(false);
          }
          showSnackbar('New material created successfully', 'success');
        }}
      />

      {/* Add New Reporting Category Dialog */}
      <CustomizingEntityDialog
        open={isReportingCategoryDialogOpen}
        entityType="reportingCategories"
        entityConfig={ENTITY_CONFIGS.reportingCategories}
        record={{
          id: '',
          code: '',
          label: '',
          requiresInternalSamplePrivilege: false,
        }}
        isEdit={false}
        onSave={handleReportingCategoryCreate}
        onClose={() => setIsReportingCategoryDialogOpen(false)}
      />
    </>
  );
};

const AddToInventoryDialog: FC<AddToInventoryDialogProps> = (props) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();
  const dialogInitKey = `${props.project?.id ?? ''}|${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(
    currentUser?.usingSignInAs ?? false
  )}`;

  if (optionsLoading) {
    return (
      <StandardDialog
        open={props.open}
        onClose={props.onClose}
        title="Add to Inventory"
        maxWidth="lg"
        onSubmit={() => {}}
        submitText="Add"
        submitDisabled
        helpDocKey="dialog.addToInventory"
      >
        <Box sx={{ p: 2 }}>Loading options...</Box>
      </StandardDialog>
    );
  }

  return <AddToInventoryDialogInner key={dialogInitKey} {...props} options={options} />;
};

export { AddToInventoryDialog };
